import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Sparkles } from 'lucide-react';
import { LockedInsight } from 'modules/examInsights';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { requestWritingFeedback, type WritingFeedbackInfo, type WritingMarkInfo } from '../api';
import { analysisLanguage } from '../language';

export interface WritingFeedbackProps {
  attemptId: string;
  questionId: string;
  /** The answer text as stored: the marks point into it. */
  essay: string;
  /** What was already written for this answer. */
  saved: WritingFeedbackInfo | null;
  /** Locked marketplace review: the feedback comes with the plan. */
  locked: boolean;
  className?: string;
}

const MARK_STYLE: Record<WritingMarkInfo['kind'], string> = {
  good: 'bg-[#CFEFD9]',
  suggest: 'bg-[#FBE9A8]',
  error: 'bg-[#FAD0CC]',
};
const NOTE_CHIP: Record<WritingMarkInfo['kind'], string> = {
  good: 'bg-[#E6F4EC] text-[#166534]',
  suggest: 'bg-[#FFF4D6] text-[#92400E]',
  error: 'bg-[#FDECEC] text-[#B42318]',
};

/** The essay as paragraphs, with each mark's span highlighted and numbered. Spans that do not fit the text are skipped. */
export function AnnotatedEssay({ essay, marks }: { essay: string; marks: WritingMarkInfo[] }) {
  const pieces: React.ReactNode[] = [];
  let cursor = 0;
  marks.forEach((mark, i) => {
    if (mark.start < cursor || mark.end > essay.length || mark.end <= mark.start) return;
    if (mark.start > cursor) pieces.push(essay.slice(cursor, mark.start));
    pieces.push(
      <mark key={i} className={cn('rounded-[3px] px-0.5 py-px text-inherit', MARK_STYLE[mark.kind])}>
        {essay.slice(mark.start, mark.end)}
        <sup className="ml-0.5 font-sans text-[10px] font-extrabold text-[#10182B]">{i + 1}</sup>
      </mark>,
    );
    cursor = mark.end;
  });
  if (cursor < essay.length) pieces.push(essay.slice(cursor));
  return <p className="whitespace-pre-wrap font-serif text-[16px] leading-[1.85] text-foreground">{pieces}</p>;
}

/** The i18n key for what went wrong, from the API's error code. Anything unrecognised is a plain failure. */
function failureKey(error: unknown): string {
  const code = (error as { response?: { data?: { code?: unknown } } } | null)?.response?.data?.code;
  if (code === 'WRITING_TOO_SHORT') return 'writing_feedback.too_short';
  if (code === 'RESULTS_HIDDEN') return 'writing_feedback.unavailable';
  return 'writing_feedback.failed';
}

/**
 * Under an IELTS Writing answer: the essay with the AI's marks and a numbered note for each, then a Band 8 answer to
 * the same task. Written once, when the learner asks; locked, it is two gates and nothing behind them.
 */
export function WritingFeedback({ attemptId, questionId, essay, saved, locked, className }: WritingFeedbackProps) {
  const { t, i18n } = useTranslation();
  const language = analysisLanguage(i18n.language);
  const queryClient = useQueryClient();
  const key = ['writing-feedback', attemptId, questionId];

  // Not fetched here: the text arrives from the result payload or from the mutation below.
  const { data: feedback } = useQuery<WritingFeedbackInfo | null>({
    queryKey: key,
    queryFn: () => Promise.resolve(saved),
    initialData: saved,
    enabled: false,
    staleTime: Infinity,
  });

  // A ref, not `isPending`: two clicks in the same tick both see the old render.
  const inFlight = useRef(false);
  const create = useMutation({
    mutationFn: () => requestWritingFeedback(attemptId, questionId, language),
    onSuccess: (result) => queryClient.setQueryData(key, result),
    onSettled: () => {
      inFlight.current = false;
    },
  });

  if (locked) {
    return (
      <div className={className}>
        <LockedInsight title={t('writing_feedback.marks_title')} description={t('writing_feedback.marks_locked_body')} lines={5} className="mt-4" />
        <LockedInsight title={t('writing_feedback.model_title')} description={t('writing_feedback.model_locked_body')} lines={4} className="mt-3" />
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className={cn('mt-4', className)}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={create.isPending}
          onClick={() => {
            if (inFlight.current) return;
            inFlight.current = true;
            create.mutate();
          }}
        >
          {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : <Sparkles className="mr-2 h-4 w-4" aria-hidden />}
          {create.isPending ? t('writing_feedback.working') : t('writing_feedback.button')}
        </Button>
        {create.isError ? (
          <p role="alert" className="mt-2 text-sm text-destructive">
            {t(failureKey(create.error))}
          </p>
        ) : null}
      </div>
    );
  }

  const kindLabel: Record<WritingMarkInfo['kind'], string> = {
    good: t('writing_feedback.kind_good'),
    suggest: t('writing_feedback.kind_suggest'),
    error: t('writing_feedback.kind_error'),
  };
  return (
    <div className={className}>
      <section aria-label={t('writing_feedback.marks_title')} className="mt-4 rounded-[22px] border border-[#E6E9F2] bg-card p-[18px] dark:border-border sm:p-6">
        <h3 className="font-display text-[17px] font-extrabold tracking-[-0.03em] text-foreground sm:text-xl">{t('writing_feedback.marks_title')}</h3>
        <div className="mt-4">
          <AnnotatedEssay essay={essay} marks={feedback.annotations} />
        </div>
        <ol className="mt-5 border-t border-[#EEF1F8] dark:border-border">
          {feedback.annotations.map((mark, i) => (
            <li key={i} className="flex gap-2.5 border-t border-[#EEF1F8] py-3 text-[13px] leading-normal first:border-t-0 dark:border-border">
              <span className={cn('mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[11px] font-extrabold', NOTE_CHIP[mark.kind])}>{i + 1}</span>
              <span>
                <b>{kindLabel[mark.kind]}:</b> {mark.note}
              </span>
            </li>
          ))}
        </ol>
      </section>
      <section aria-label={t('writing_feedback.model_title')} className="mt-3 rounded-[22px] border border-[#E4D9FF] bg-[#F8F5FF] p-[18px] dark:border-border dark:bg-card sm:p-6">
        <h3 className="font-display text-[17px] font-extrabold tracking-[-0.03em] text-foreground sm:text-xl">{t('writing_feedback.model_title')}</h3>
        <p className="mt-3 whitespace-pre-wrap font-serif text-[15.5px] leading-[1.8] text-[#364055] dark:text-foreground/90">{feedback.modelAnswer}</p>
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{t('writing_feedback.note')}</p>
      </section>
    </div>
  );
}
